import {
  showNewPaymentMethodForm,
  savePaymentMethodFieldsFailure,
  hideNewPaymentMethodForm
} from 'reduxModules/forms/paymentMethod'
import {
  activateSubscribingMode,
  deactivateSubscribingMode,
  validateAddress,
  lifetimePreviewClear,
  types as checkoutTypes
} from 'reduxModules/checkout'
import { clearDiscountCode } from 'reduxModules/discountCode'
import { initConfirmation } from 'reduxModules/confirmation'
import {
  seedLifetimeFromPlanList,
  fetchLifetimePreview
} from 'reduxModules/checkout/dispatchers'
import { planSelectedSelector } from 'reduxModules/checkout/selectors'
import _ from 'lodash'
import modalDispatchers from 'reduxModules/generic/modal/dispatchers'
import {
  subscriptionPlansFetch,
  subscriptionsFetch
} from 'reduxModules/subscriptions'
import { clearRecaptchaToken, validateUpgradeCoupon } from 'reduxModules/app'
import { navigate } from '../../../utils/navigation'
import {
  getUserProfile,
  getUserTutorials,
  getUserFavorites,
  updateUserProfile,
  createUserAddress,
  updateUserAddress,
  getUserAddresses,
  removeUserAddress,
  getUserPaymentMethods,
  markDefaultUserPaymentMethod,
  removePaymentMethod,
  getUserBraintreeToken,
  savePaymentMethod,
  savePaymentMethodFailure,
  editingAddress,
  types as userTypes,
  getUserOrders,
  getOrderDetail,
  showAddressFormAction,
  hideAddressFormAction,
  changePlan,
  getUserLearningPaths,
  closeProfile,
  closeExternalUserFunnelDialog
} from './'
import notifications, {
  toastError,
  toastServerError,
  toastSuccess
} from '../../../global/notifications'

import fixturePlansLegacy from '../../../components/SubscriptionPlans/fixturePlansLegacy'

export const braintreeErrorHandler = (dispatch, error) => {
  if (error.code === 'HOSTED_FIELDS_FIELDS_INVALID')
    dispatch(savePaymentMethodFieldsFailure(error))
  else {
    dispatch(savePaymentMethodFailure(error))
    toastServerError(error.message)
  }
}

export const profileDispatchers = (dispatch) => ({
  loadUserProfile() {
    dispatch(getUserProfile())
  },
  onSubmit(data) {
    data = { ...data }
    Object.keys(data).forEach((key) => {
      if (key.startsWith('movie')) {
        data[`movie_${key.substr(5)}`] = data[key] !== '' ? data[key] : null
        delete data[key]
      }
    })
    dispatch(updateUserProfile(data))
      .then(([_, res]) => {
        if (res.type === userTypes.USERS_PROFILE_UPDATE_SUCCESS) {
          toastSuccess(notifications.profileUpdate)
          dispatch(closeProfile())
        } else toastError(notifications.profileUpdate)
      })
      .catch((error) => toastServerError(error.errors))
  },
  updateInterests(data) {
    dispatch(updateUserProfile(data))
      .then(([_, res]) => {
        if (res.type === userTypes.USERS_PROFILE_UPDATE_SUCCESS)
          toastSuccess(notifications.interestsUpdate)
        else toastError(notifications.interestsUpdate)
      })
      .catch((error) => toastServerError(error.errors))
  }
})

export const tutorialsDispatchers = (dispatch) => ({
  loadUserTutorials() {
    dispatch(getUserTutorials())
  }
})

export const favoritesDispatchers = (dispatch) => ({
  loadFavorites() {
    dispatch(getUserFavorites())
  }
})

export const addressDispatchers = (dispatch) => ({
  onSubmit(data, dispatch, props) {
    import('reduxModules/utils/getCountryCode').then((getCountryCode) => {
      const countryCodeAlpha2 = getCountryCode.default(data.countryName)
      const { editing, values } = props
      const newData = {
        ..._.omitBy(data, _.isNil),
        countryCodeAlpha2,
        dismissWeightValidation: true
      }
      dispatch(validateAddress({ ...newData, id: undefined }))
        .then(([_, res]) => {
          if (res.type === checkoutTypes.VALIDATE_ADDRESS_SUCCESS) {
            const action = editing
              ? updateUserAddress(values.id)(newData)
              : createUserAddress(newData)

            dispatch(action).then(([_, res]) => {
              if (res.type === userTypes.USERS_ADDRESS_CREATE_SUCCESS)
                toastSuccess(notifications.addressCreated)
              else if (res.type === userTypes.USERS_ADDRESS_UPDATE_SUCCESS)
                toastSuccess(notifications.addressUpdated)
              else if (
                res.type === userTypes.USERS_ADDRESS_CREATE_REQUEST_FAILURE
              )
                toastError(notifications.addressCreated)
              else if (
                res.type === userTypes.USERS_ADDRESS_UPDATE_REQUEST_FAILURE
              )
                toastError(notifications.addressUpdated)
              dispatch(hideAddressFormAction())
            })
          }
        })
        .catch((payload) => {
          toastServerError(payload.errors)
        })
    })
  },
  showAddressForm: () => dispatch(showAddressFormAction()),
  hideAddressForm: () => dispatch(hideAddressFormAction())
})

export const addressesDispatchers = (dispatch) => ({
  loadAddresses() {
    dispatch(getUserAddresses())
  },
  removeAddress(pk, activeAddressFormId) {
    const confirmation = {
      title: 'Warning',
      text: 'Are you sure you want to remove this address?',
      options: [
        { resolveValue: 'remove', text: 'Remove', class: 'confirm-btn' }
      ]
    }
    dispatch(initConfirmation(confirmation)).on('remove', () =>
      dispatch(removeUserAddress(pk)).then(([_, res]) => {
        if (res.type === userTypes.USERS_ADDRESS_REMOVE_SUCCESS) {
          toastSuccess(notifications.addressRemoved)
          if (pk === activeAddressFormId) dispatch(hideAddressFormAction())
        } else toastError(notifications.addressRemoved)
      })
    )
  },
  editAddress(address) {
    dispatch(editingAddress(address))
  },
  ...modalDispatchers(dispatch)
})

export const dispatchGetUserBraintreeToken = (dispatch) =>
  dispatch(getUserBraintreeToken())

export const paymentMethodListDispatchers = (dispatch) => ({
  loadPaymentMethods() {
    dispatch(getUserPaymentMethods())
  },
  markPaymentDefault(token) {
    dispatch(markDefaultUserPaymentMethod(token)()).then(([_, res]) => {
      if (res.type === userTypes.USERS_MARK_PAYMENT_METHOD_DEFAULT_SUCCESS)
        toastSuccess(notifications.paymentMethodSetAsDefault)
      else toastError(notifications.paymentMethodSetAsDefault)
    })
  },
  markPaymentDefaultWithconfirmation(token) {
    const confirmation = {
      title: 'Warning',
      text: 'Your subscription will now be charged to this card',
      options: [
        { resolveValue: 'accept', text: 'Accept', class: 'confirm-btn' }
      ]
    }
    dispatch(initConfirmation(confirmation)).on('accept', () =>
      this.markPaymentDefault(token)
    )
  },
  removePaymentMethodDispatcher(token) {
    const confirmation = {
      title: 'Warning',
      text: 'Are you sure you want to remove this payment method?',
      options: [
        { resolveValue: 'remove', text: 'Remove', class: 'confirm-btn' }
      ]
    }
    dispatch(initConfirmation(confirmation)).on('remove', () =>
      dispatch(removePaymentMethod(token)).then(([_, res]) => {
        if (res.type === userTypes.USERS_PAYMENT_METHOD_REMOVE_SUCCESS)
          toastSuccess(notifications.paymentMethodDeleted)
        else toastError(notifications.paymentMethodDeleted)
      })
    )
  },
  ...modalDispatchers(dispatch)
})

export const dispatchSavePaymentMethod = (dispatch, payload, recaptcha) => {
  dispatch(savePaymentMethod(recaptcha)(payload))
    .then(([_, res]) => {
      if (res.type === userTypes.USERS_PAYMENT_METHOD_SAVE_SUCCESS) {
        toastSuccess(notifications.paymentMethodCreated)
        dispatch(hideNewPaymentMethodForm())
      } else toastError(notifications.paymentMethodCreated)
    })
    .catch((error) => toastServerError(error.errors))
}

export const paymentMethodsDispatchers = (
  stateProps,
  dispatchProps,
  ownProps
) => {
  const { dispatch } = dispatchProps
  return {
    ...stateProps,
    ...ownProps,
    loadPaymentMethods() {
      dispatch(getUserPaymentMethods())
    },
    showNewPaymentMethodForm: () => dispatch(showNewPaymentMethodForm()),
    getBraintreeClientToken: () => {
      const { clientToken } = stateProps
      if (clientToken === '') dispatchGetUserBraintreeToken(dispatch)
    },
    savePaymentMethod(deviceData, tokenize, recaptcha) {
      tokenize()
        .then((payload) => {
          dispatchSavePaymentMethod(
            dispatch,
            {
              deviceData,
              paymentMethodNonce: payload.nonce
            },
            recaptcha
          )
        })
        .catch((error) => braintreeErrorHandler(dispatch, error))
        .finally(() => dispatch(clearRecaptchaToken()))
    },
    hideNewPaymentMethodForm: () => dispatch(hideNewPaymentMethodForm()),
    showFraudProtectionError() {
      toastError(notifications.fraudProtection)
    }
  }
}

export const ordersDispatchers = (dispatch) => ({
  loadOrders() {
    dispatch(getUserOrders())
  },
  loadSubscriptions() {
    dispatch(subscriptionsFetch())
  }
})

export const orderDetailDispatchers = (dispatch) => ({
  loadOrderDetail: (pk) => dispatch(getOrderDetail(pk)())
})

export const changeSubscriptionDispatchers = (dispatch) => ({
  dispatchUpgradePlan: (plans, newPlan, oldPlan, preAppliedCoupon) => {
    const isProrated = !(
      _.size(
        _.filter(fixturePlansLegacy, (p) => p.firstMonthPrice !== null)
      ) === 0
    )

    // Prefer the plans array passed in from the page (which carries the
    // user's *effective* current price) and only fall back to the static
    // fixturePlansLegacy for ranks not present there (e.g. Basic).
    const planInfo =
      _.find(plans, { rank: oldPlan }) ||
      _.find(fixturePlansLegacy, { rank: oldPlan })
    if (!planInfo) {
      toastError(notifications.unupgradeableSubscription)
      return
    }

    const isLifetime = newPlan.name === 'Lifetime'

    if (isLifetime) {
      const confirmation = {
        title: 'Upgrade subscription plan',
        discountCode: false,
        options: [
          { resolveValue: 'accept', text: 'continue', class: 'confirm-btn' }
        ],
        height: 800,
        newPlan,
        planInfo,
        isProrated: false,
        preAppliedCoupon: null,
        cancelCallback: () => {
          dispatch(deactivateSubscribingMode())
          dispatch(lifetimePreviewClear())
        },
        componentId: 'upgrade'
      }
      dispatch(initConfirmation(confirmation)).on('accept', () => {
        // Preview was fetched while dialog was open; /checkout will read it
        // from state.checkout.lifetimePreview and short-circuit a re-fetch
        // when planSelected.id matches.
        navigate('/checkout')
      })
      // Resolve real backend plan id, then fetch the upgrade preview so the
      // dialog can display upgrade credit + final charge inline.
      dispatch(async (d, getState) => {
        const seeded = await d(seedLifetimeFromPlanList())
        if (!seeded) return
        const planSelected = planSelectedSelector(getState())
        if (planSelected && planSelected.id)
          await d(fetchLifetimePreview(planSelected.id))
      })
      return
    }

    const confirmation = {
      title: 'Upgrade subscription plan',
      discountCode: !preAppliedCoupon,
      options: [
        { resolveValue: 'accept', text: 'upgrade', class: 'confirm-btn' }
      ],
      height: 820,
      newPlan,
      planInfo,
      isProrated,
      preAppliedCoupon,
      cancelCallback: () => {
        dispatch(deactivateSubscribingMode())
        dispatch(clearDiscountCode())
      },
      componentId: 'upgrade'
    }
    dispatch(activateSubscribingMode(newPlan))
    dispatch(initConfirmation(confirmation)).on('accept', (coupon) => {
      const request = { newPlan: newPlan.id }
      if (coupon) request.coupon = coupon.codename
      return dispatch(changePlan(request))
        .then(([_, res]) => {
          if (res.type === userTypes.USERS_CHANGE_PLAN_SUCCESS) {
            toastSuccess(notifications.upgradeSubscription)
            dispatch(subscriptionPlansFetch())
            dispatch(clearDiscountCode())
          } else toastError(notifications.upgradeSubscription)
        })
        .catch(() => toastError(notifications.upgradeSubscription))
    })
  },
  fetchUpgradeCoupon: (codename, plans) =>
    dispatch(validateUpgradeCoupon(codename, plans)()).then(([_, res]) => {
      if (res.type === 'VALIDATE_UPGRADE_COUPON_SUCCESS') return res.payload
      throw new Error('Invalid coupon')
    }),
  ...modalDispatchers(dispatch)
})

export const learningPathsDispatchers = (dispatch) => ({
  loadUserLearningPaths() {
    dispatch(getUserLearningPaths())
  }
})

export const walkthroughDispatchers = (dispatch) => ({
  updateWatchedWalkthrough() {
    dispatch(updateUserProfile({ watchedWalkthroughVideo: true }))
  }
})

export const externalNewsletterDispatchers = (dispatch) => ({
  updateDeclinedExternalNewsletter() {
    dispatch(updateUserProfile({ declinedNewsletter: true }))
  },
  closeDialog() {
    dispatch(closeExternalUserFunnelDialog())
  },
  onSubmit: (data) => {
    dispatch(updateUserProfile({ alternativeEmail: data?.email }))
      .then(([_, res]) => {
        if (res.type === userTypes.USERS_PROFILE_UPDATE_SUCCESS) {
          dispatch(closeExternalUserFunnelDialog())
          toastSuccess(notifications.externalFunnel)
        } else toastError(notifications.externalFunnel)
      })
      .catch((payload) => {
        const error = payload.errors
        toastServerError(error)
      })
  }
})
